import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, ScanBarcode, CheckCircle, X, Flame, Package, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// Auto-submit the queue every time this many USNs are scanned. Keeps batches
// small so rejected/held-up pieces surface immediately (a popup) instead of
// getting lost inside a 200-scan submit and slipping to the next section.
const BATCH_SIZE = 25;

const Pressing = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const usnInputRef = useRef(null);

  // Step: 'staff' | 'scanning' | 'result'
  const [step, setStep] = useState('staff');

  // Staff + session
  const [staffValue, setStaffValue] = useState('');
  const [staff, setStaff] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [validating, setValidating] = useState(false);

  // Client-side queue (pending, not yet submitted) — instant, no API per scan
  const [usnValue, setUsnValue] = useState('');
  const [queue, setQueue] = useState([]);          // [{ usn_code }]
  const [rejected, setRejected] = useState([]);    // [{ usn_code, reason }]

  // Running session tallies (pressed via batch submits)
  const [pressedTotal, setPressedTotal] = useState(0);
  const [bundlesTotal, setBundlesTotal] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [result, setResult] = useState(null);

  // Blocking popup that surfaces held-up (rejected) USNs after each auto-submit
  const [rejectPopup, setRejectPopup] = useState({ open: false, items: [] });

  const focusUsn = useCallback(() => {
    setTimeout(() => usnInputRef.current?.focus(), 80);
  }, []);

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;

    setValidating(true);
    try {
      const resp = await apiClient.post('/pressing/start-session', { staff_id: id });
      setStaff({ staff_id: resp.data.staff_id, staff_name: resp.data.staff_name });
      setSessionId(resp.data.session_id);
      setStaffValue('');
      setQueue([]);
      setRejected([]);
      setBundlesTotal(0);

      if (resp.data.resumed && resp.data.scanned_count > 0) {
        setPressedTotal(resp.data.scanned_count);
        toast.info(`Resumed session — ${resp.data.scanned_count} USNs already pressed`);
      } else {
        setPressedTotal(0);
        toast.success(`Welcome, ${resp.data.staff_name}`);
      }

      setStep('scanning');
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start session');
      setStaffValue('');
      setTimeout(() => staffInputRef.current?.focus(), 80);
    } finally {
      setValidating(false);
    }
  };

  // Submit a set of USN codes in ONE bulk request. Accepted pieces are pressed
  // and leave the queue; rejected pieces are recorded AND surfaced in a popup.
  const submitBatch = async (codes) => {
    if (!codes || codes.length === 0 || submitting) return { acceptedCount: 0, rejected: [] };
    setSubmitting(true);
    try {
      const resp = await apiClient.post('/pressing/scan-batch', {
        session_id: sessionId,
        usn_codes: codes,
      });

      const acceptedCount = resp.data.accepted_count || 0;
      const rejectedList = resp.data.rejected || [];

      setPressedTotal(resp.data.session_total ?? (pressedTotal + acceptedCount));
      setBundlesTotal((prev) => prev + (resp.data.bundles_updated || 0));

      // Remove every submitted code from the pending queue (accepted = pressed,
      // rejected = tracked separately).
      const codeSet = new Set(codes);
      setQueue((prev) => prev.filter((i) => !codeSet.has(i.usn_code)));

      if (rejectedList.length > 0) {
        // Accumulate across auto-submits (dedup by usn_code) + pop it up.
        setRejected((prev) => {
          const newCodes = new Set(rejectedList.map((r) => r.usn_code));
          return [...rejectedList, ...prev.filter((p) => !newCodes.has(p.usn_code))];
        });
        setRejectPopup({ open: true, items: rejectedList });
      }

      if (acceptedCount > 0 && rejectedList.length > 0) {
        toast.warning(`${acceptedCount} pressed · ${rejectedList.length} held up`);
      } else if (acceptedCount > 0) {
        toast.success(`${acceptedCount} USN${acceptedCount !== 1 ? 's' : ''} pressed`);
      } else {
        toast.error(`All ${rejectedList.length} USN${rejectedList.length !== 1 ? 's' : ''} held up`);
      }

      if (rejectedList.length === 0) focusUsn();
      return { acceptedCount, rejected: rejectedList };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit batch');
      return { acceptedCount: 0, rejected: [] };
    } finally {
      setSubmitting(false);
    }
  };

  // INSTANT client-side scan — auto-submits once the queue reaches BATCH_SIZE.
  const handleUsnScan = (e) => {
    e.preventDefault();
    const code = usnValue.trim();
    if (!code || submitting) return;

    if (queue.some((item) => item.usn_code === code)) {
      toast.error(`USN ${code} already in queue`);
      setUsnValue('');
      focusUsn();
      return;
    }
    const newQueue = [{ usn_code: code }, ...queue];
    setQueue(newQueue);
    setUsnValue('');

    if (newQueue.length >= BATCH_SIZE) {
      submitBatch(newQueue.map((i) => i.usn_code));
    } else {
      focusUsn();
    }
  };

  const handleRemoveQueued = (code) => {
    setQueue((prev) => prev.filter((i) => i.usn_code !== code));
  };

  const handleRemoveRejected = (code) => {
    setRejected((prev) => prev.filter((i) => i.usn_code !== code));
  };

  const closeRejectPopup = () => {
    setRejectPopup({ open: false, items: [] });
    focusUsn();
  };

  const handleFinish = async () => {
    if (finishing || submitting) return;
    setFinishing(true);
    try {
      // Flush any remaining queued USNs first so nothing is left unpressed.
      if (queue.length > 0) {
        const { rejected: flushRejected } = await submitBatch(queue.map((i) => i.usn_code));
        if (flushRejected.length > 0) {
          // Held-up pieces surfaced — make the operator acknowledge them before
          // the session can be completed (prevents silently passing them on).
          setFinishing(false);
          return;
        }
      }
      const resp = await apiClient.post('/pressing/complete-session', { session_id: sessionId });
      setResult({
        session_id: resp.data.session_id,
        total_pressed: resp.data.total_pressed ?? pressedTotal,
        bundles_updated: bundlesTotal,
        staff_name: resp.data.staff_name || staff?.staff_name || '',
      });
      toast.success(resp.data.message || 'Session completed');
      setStep('result');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete session');
    } finally {
      setFinishing(false);
    }
  };

  const handleNewSession = () => {
    setStep('staff');
    setStaff(null);
    setSessionId('');
    setStaffValue('');
    setQueue([]);
    setRejected([]);
    setPressedTotal(0);
    setBundlesTotal(0);
    setResult(null);
    setRejectPopup({ open: false, items: [] });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pressing-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">Pressing</h1>
          <p className="text-xs text-gray-500">Scan USNs — auto-submits every {BATCH_SIZE}</p>
        </div>
        {step === 'scanning' && staff && (
          <div className="flex items-center gap-2 bg-orange-50 px-3 py-1.5 rounded-lg" data-testid="staff-badge">
            <UserCheck className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-800">{staff.staff_name}</span>
            <span className="text-xs text-orange-500">({staff.staff_id})</span>
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* ============ STEP: STAFF ID ============ */}
        {step === 'staff' && (
          <>
            <Card className="border-orange-200">
              <CardHeader className="text-center pb-2">
                <Flame className="w-10 h-10 text-orange-500 mx-auto mb-2" />
                <CardTitle className="text-base">Scan Staff ID</CardTitle>
                <p className="text-sm text-gray-500">Enter your Staff ID to start pressing</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleStaffSubmit} className="flex gap-2">
                  <Input
                    ref={staffInputRef}
                    value={staffValue}
                    onChange={(e) => setStaffValue(e.target.value)}
                    placeholder="Staff ID (e.g., V0104)"
                    autoFocus
                    data-testid="staff-input"
                  />
                  <Button type="submit" disabled={validating || !staffValue.trim()} data-testid="staff-submit">
                    {validating ? '...' : 'Start'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button variant="outline" size="sm" onClick={() => navigate('/sections/inhouse/pressing/history')} data-testid="view-history-btn">
                View Pressing History
              </Button>
            </div>
          </>
        )}

        {/* ============ STEP: USN SCANNING ============ */}
        {step === 'scanning' && (
          <>
            {/* Session info + running tallies */}
            <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-sm">
              <span className="font-mono text-orange-700">{sessionId}</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="pressed-total-badge">
                  {pressedTotal} pressed
                </Badge>
                <Badge className="bg-orange-100 text-orange-800 border-orange-200" data-testid="queue-count">
                  {queue.length} / {BATCH_SIZE} queued
                </Badge>
              </div>
            </div>

            {/* USN scan input (instant, client-side, auto-submits at BATCH_SIZE) */}
            <Card>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Scan USN Code</label>
                <form onSubmit={handleUsnScan} className="flex gap-2">
                  <Input
                    ref={usnInputRef}
                    value={usnValue}
                    onChange={(e) => setUsnValue(e.target.value)}
                    placeholder={submitting ? 'Pressing batch…' : 'Scan USN barcode...'}
                    disabled={submitting}
                    autoFocus
                    data-testid="usn-scan-input"
                  />
                  <Button type="submit" disabled={!usnValue.trim() || submitting} data-testid="usn-scan-submit">
                    <ScanBarcode className="w-4 h-4" />
                  </Button>
                </form>
                <p className="text-xs text-gray-400 mt-1.5">
                  Scans queue instantly and auto-submit every {BATCH_SIZE} — any held-up pieces pop up immediately.
                </p>
              </CardContent>
            </Card>

            {/* Rejected USNs (stay until deleted) */}
            {rejected.length > 0 && (
              <Card className="border-red-200 bg-red-50/40" data-testid="rejected-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    Held up — NOT pressed ({rejected.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {rejected.map((item) => (
                      <div key={item.usn_code} className="flex items-center justify-between bg-white border border-red-100 rounded px-3 py-1.5 text-sm" data-testid={`rejected-usn-${item.usn_code}`}>
                        <div className="min-w-0">
                          <span className="font-mono font-medium text-red-800">{item.usn_code}</span>
                          <p className="text-xs text-red-500 break-words" data-testid={`rejected-reason-${item.usn_code}`}>{item.reason}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 shrink-0" onClick={() => handleRemoveRejected(item.usn_code)} data-testid={`rejected-delete-${item.usn_code}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pending queue list */}
            {queue.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Queued USNs ({queue.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {queue.map((item) => (
                      <div key={item.usn_code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm" data-testid={`queued-usn-${item.usn_code}`}>
                        <div className="flex items-center gap-2">
                          <ScanBarcode className="w-3.5 h-3.5 text-orange-400" />
                          <span className="font-mono font-medium">{item.usn_code}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600" onClick={() => handleRemoveQueued(item.usn_code)} data-testid={`queued-delete-${item.usn_code}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit remaining batch (partial < BATCH_SIZE) */}
            <Button
              className="w-full bg-orange-600 hover:bg-orange-700"
              size="lg"
              onClick={() => submitBatch(queue.map((i) => i.usn_code))}
              disabled={submitting || queue.length === 0}
              data-testid="submit-pressing-btn"
            >
              {submitting ? 'Pressing...' : (
                <>
                  <Flame className="w-4 h-4 mr-2" />
                  Submit — Press {queue.length} USN{queue.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>

            {/* Finish session */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleFinish}
              disabled={finishing || submitting}
              data-testid="finish-session-btn"
            >
              {finishing ? 'Finishing...' : 'Finish Session'}
            </Button>
          </>
        )}

        {/* ============ STEP: RESULT ============ */}
        {step === 'result' && result && (
          <Card className="border-green-200 bg-green-50/30" data-testid="pressing-result-card">
            <CardContent className="pt-6">
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h2 className="text-lg font-bold text-green-800">Pressing Complete</h2>
                <p className="text-sm text-gray-600 font-mono">{result.session_id}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">USNs Pressed</p>
                  <p className="text-xl font-bold text-orange-600" data-testid="result-total-pressed">{result.total_pressed}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Bundles Completed</p>
                  <p className="text-xl font-bold">{result.bundles_updated}</p>
                </div>
                <div className="col-span-2 bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Staff</p>
                  <p className="font-bold text-orange-700">{result.staff_name}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <Button className="w-full" onClick={handleNewSession} data-testid="new-session-btn">
                  Start New Session
                </Button>
                <Button variant="outline" className="w-full" onClick={() => navigate('/sections/inhouse/pressing/history')}>
                  View History
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ============ HELD-UP (REJECTED) POPUP ============ */}
      <Dialog open={rejectPopup.open} onOpenChange={(o) => { if (!o) closeRejectPopup(); }}>
        <DialogContent data-testid="pressing-reject-popup">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {rejectPopup.items.length} USN{rejectPopup.items.length !== 1 ? 's' : ''} held up — NOT pressed
            </DialogTitle>
            <DialogDescription>
              These pieces were rejected and are <b>NOT pressed</b>. Do not pass them to the next section —
              set them aside and resolve the issue, otherwise they will get stuck in Assortment Packing.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {rejectPopup.items.map((it) => (
              <div key={it.usn_code} className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm" data-testid={`popup-rejected-${it.usn_code}`}>
                <span className="font-mono font-semibold text-red-800">{it.usn_code}</span>
                <p className="text-xs text-red-500 break-words">{it.reason}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button className="bg-red-600 hover:bg-red-700 text-white w-full" onClick={closeRejectPopup} data-testid="pressing-reject-popup-ack">
              OK — I&apos;ll set these {rejectPopup.items.length} piece{rejectPopup.items.length !== 1 ? 's' : ''} aside
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pressing;
